import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from '../components/Layout/AdminLayout';
import { FaEye, FaUser, FaMobileAlt, FaPills, FaTag, FaRegWindowClose, FaCalendarAlt, FaSearch } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

// Sold table ki fields ke liye interface
// Status field is removed as per user request (verification is done)
interface SoldLead {
  // Use the ID returned by the API (e.g., sale_orders.id). This is critical for the key.
  id: string; 

  // Customer/Mobile Info (from sale_orders)
  firstName?: string; // Mapped to sale_orders.customer_name
  mobile?: string;    // sale_orders.mobile
  alternateMobile?: string; // sale_orders.alternate_mobile
  
  // Statuses (from sale_orders)
  orderStatus?: 'DELIVERED' | 'SHIPPED' | 'PROCESSING' | 'PENDING' | string; 
  paymentStatus?: 'PAID' | 'UNPAID' | string;
  shippingStatus?: 'DELIVERED' | 'IN TRANSIT' | 'OUT FOR DELIVERY' | 'READY FOR DISPATCH' | string; 
  
  // Product/Item Info (from a joined sale_order_items)
  medicine?: string; // Mapped to sale_order_items.product_name
  medicineQuantity?: number; // sale_order_items.quantity
  discountPercentage?: number; // sale_order_items.discount_percentage
  payableAmount?: number; // sale_orders.total_payable_amount
  createdAt?: string; // Mapped to sale_orders.order_date or created_at timestamp

  // Details for Modal 
  details?: {
    fullAddress?: string; 
    shippingStatus?: string;
    paymentStatus?: string;
    otherNotes?: string; 
  };
}

// ----------------------------------------------------------------------
// MODAL COMPONENT (for full details)
// ----------------------------------------------------------------------
interface DetailsModalProps {
    order: SoldLead;
    onClose: () => void;
}

const DetailsModal: React.FC<DetailsModalProps> = ({ order, onClose }) => {
    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl transform transition-all duration-300 scale-100">
                
                {/* Modal Header */}
                <div className="flex justify-between items-center p-5 border-b border-gray-700 bg-gray-900 rounded-t-xl">
                    <h3 className="text-2xl font-bold text-white flex items-center">
                        <FaEye className="mr-3 text-blue-400"/> Order Details: <span className="text-red-400 ml-2">#{order.id?.substring(0, 8) || 'N/A'}...</span>
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-white transition duration-200">
                        <FaRegWindowClose className="w-6 h-6"/>
                    </button>
                </div>

                {/* Modal Body */}
                <div className="p-6 space-y-5 text-gray-300">
                    
                    {/* Customer Info */}
                    <div className="p-4 bg-gray-700/50 rounded-lg">
                        <h4 className="font-semibold text-lg mb-2 text-white border-b border-gray-600 pb-1">Customer Information</h4>
                        <p className="flex items-center"><FaUser className="mr-2 text-red-400"/> **Name:** {order.firstName || 'N/A'}</p>
                        <p className="flex items-center"><FaMobileAlt className="mr-2 text-red-400"/> **Primary Mobile:** {order.mobile || 'N/A'}</p>
                        <p className="flex items-center"><FaMobileAlt className="mr-2 text-red-400"/> **Alternate Mobile:** {order.alternateMobile || 'N/A'}</p>
                        <p>**Full Address:** {order.details?.fullAddress || 'N/A'}</p>
                    </div>

                    {/* Order & Payment */}
                    <div className="p-4 bg-gray-700/50 rounded-lg grid grid-cols-2 gap-4">
                        <div>
                            <h4 className="font-semibold text-lg mb-2 text-white border-b border-gray-600 pb-1">Order Details</h4>
                            <p className="flex items-center"><FaPills className="mr-2 text-blue-400"/> Product: {order.medicine || 'N/A'}</p>
                            <p className="flex items-center"><FaTag className="mr-2 text-blue-400"/> Quantity: {order.medicineQuantity || 0}</p>
                            <p className="flex items-center"><FaCalendarAlt className="mr-2 text-blue-400"/> Date: {order.createdAt ? new Date(order.createdAt).toLocaleString() : 'N/A'}</p>
                        </div>
                        <div>
                            <h4 className="font-semibold text-lg mb-2 text-white border-b border-gray-600 pb-1">Financial & Status</h4>
                            <p>Discount: {order.discountPercentage || 0}%</p>
                            <p className="text-xl font-bold text-green-400">Payable: ₹{order.payableAmount?.toFixed(2) || '0.00'}</p>
                            <p>Order Status: {order.orderStatus || 'N/A'}</p>
                            <p>Shipping Status: {order.shippingStatus || 'N/A'}</p>
                            <p>Payment Status: {order.paymentStatus || 'N/A'}</p>
                        </div>
                    </div>
                    
                    <p className="p-3 bg-gray-900 rounded-lg text-sm italic">Notes: {order.details?.otherNotes || 'No additional notes provided.'}</p>
                </div>

                {/* Modal Footer */}
                <div className="p-4 border-t border-gray-700 flex justify-end">
                    <button 
                        onClick={onClose} 
                        className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

// ----------------------------------------------------------------------
// MAIN SaleOrder COMPONENT
// ----------------------------------------------------------------------
const SaleOrder: React.FC = () => {
  // States
  const [soldLeads, setSoldLeads] = useState<SoldLead[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  // verificationFilter state is removed
  const [selectedOrder, setSelectedOrder] = useState<SoldLead | null>(null); 
  const navigate = useNavigate();

  // FILTER States
  const [searchTerm, setSearchTerm] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState<'All Order Statuses' | SoldLead['orderStatus']>('All Order Statuses');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<'All Payment Statuses' | SoldLead['paymentStatus']>('All Payment Statuses');
  const [shippingStatusFilter, setShippingStatusFilter] = useState<'All Shipping Statuses' | SoldLead['shippingStatus']>('All Shipping Statuses');


  // 1. Data Fetching Function (Simplified for single endpoint)
  const fetchSoldLeads = useCallback(async () => { // Removed filter argument
    setLoading(true);
    setError(null);
    try {
      const authToken = localStorage.getItem('token');
      // Assuming your consolidated API endpoint is now sale-orders or all-orders
      const endpoint = `https://ayushvatika.com/api/admin/sale-orders`; 
      // const endpoint = `http://localhost:8484/api/admin/sale-orders`; 

      const response = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${authToken}` },
      });

      if (!response.ok) {
        if (response.status === 401) { 
          localStorage.removeItem('token');
          navigate('/login');
          throw new Error('Session Expired. Please log in again.');
        }
        throw new Error(`API Failed. Check Network tab. Status: ${response.status}`);
      }

      const data: SoldLead[] = await response.json();
      
      const validData = data.filter(order => order.id);

      setSoldLeads(validData);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not fetch sold orders. Check your API server (8484) is running.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);
  
  useEffect(() => {
    fetchSoldLeads(); // Call without filter
  }, [fetchSoldLeads]); 

  // handleVerifyOrder function is removed
  
  // 2. Modal Handler
  const handleViewDetails = (order: SoldLead) => {
      setSelectedOrder(order);
  };
  
  // 3. Client-Side Filtering Logic (MOST ROBUST VERSION)
  const filteredOrders = soldLeads.filter(order => {
    // Requires ID to be present
    if (!order.id) return false; 
    
    // Search Term Filter 
    const lowerCaseSearch = searchTerm.toLowerCase();
    const matchesSearch = (
      // Safely access fields for search
      order.id.toLowerCase().includes(lowerCaseSearch) ||
      (order.firstName || '').toLowerCase().includes(lowerCaseSearch) || // Use || '' to ensure it's always a string
      (order.mobile || '').includes(lowerCaseSearch) ||
      (order.medicine || '').toLowerCase().includes(lowerCaseSearch)
    );
    if (!matchesSearch) return false;
    
    // Dropdown Filters - Safely check status
    if (orderStatusFilter !== 'All Order Statuses' && order.orderStatus !== orderStatusFilter) return false;
    if (paymentStatusFilter !== 'All Payment Statuses' && order.paymentStatus !== paymentStatusFilter) return false;
    if (shippingStatusFilter !== 'All Shipping Statuses' && order.shippingStatus !== shippingStatusFilter) return false;
    
    return true;
  });

  const finalOrderCount = filteredOrders.length;
  
  // Utility function for table date/time formatting
  const formatDateTime = (dateString: string | undefined) => {
    try {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      const datePart = date.toLocaleDateString('en-GB'); 
      const timePart = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
      return `${datePart}, ${timePart}`;
    } catch {
      return 'N/A';
    }
  };
  
  // Utility for status badge
  const getStatusBadge = (status: string | undefined, baseColor: 'green' | 'red' | 'yellow' | 'blue' | 'gray') => {
      const displayStatus = status || 'N/A';
      
      let bgColor;
      if (baseColor === 'green') bgColor = 'bg-green-600';
      else if (baseColor === 'red') bgColor = 'bg-red-600';
      else if (baseColor === 'yellow') bgColor = 'bg-yellow-500';
      else if (baseColor === 'blue') bgColor = 'bg-blue-600';
      else bgColor = 'bg-gray-500';

      return (
          <span 
              className={`px-3 py-1 text-xs font-bold uppercase rounded-full text-white ${bgColor}`}
          >
              {displayStatus}
          </span>
      );
  };

  // Utility to determine badge color based on status text (SAFE)
  const getStatusColor = (status: string | undefined): 'green' | 'red' | 'yellow' | 'blue' | 'gray' => {
      if (!status || typeof status !== 'string') {
          return 'gray'; 
      }
      
      const upperStatus = status.toUpperCase();
      if (upperStatus === 'DELIVERED' || upperStatus === 'PAID') return 'green';
      if (upperStatus === 'SHIPPED' || upperStatus === 'IN TRANSIT') return 'blue';
      if (upperStatus === 'PROCESSING' || upperStatus === 'OUT FOR DELIVERY') return 'yellow';
      if (upperStatus === 'PENDING' || upperStatus === 'UNPAID' || upperStatus === 'READY FOR DISPATCH') return 'red';
      return 'gray';
  };


  return (
    <AdminLayout>
      <div className="container mx-auto p-8">
        
        <div className="flex justify-between items-center mb-6">
             <h2 className="text-3xl font-bold text-white">All Sales Orders</h2>
        </div>

        {/* Filter Bar */}
        <div className="bg-gray-800 p-4 rounded-lg shadow-md mb-6 border border-gray-700">
            
            <div className="flex flex-wrap gap-4 items-center">
                {/* Search Bar */}
                <div className="relative flex-grow min-w-[250px] md:min-w-[300px]">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by ID, Customer, Product, Amount..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:ring-red-500 focus:border-red-500"
                    />
                </div>
                
                {/* Status Filters */}
                <select
                    value={orderStatusFilter}
                    onChange={(e) => setOrderStatusFilter(e.target.value as typeof orderStatusFilter)}
                    className="px-4 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg cursor-pointer"
                >
                    <option value="All Order Statuses">All Order Statuses</option>
                    <option value="DELIVERED">DELIVERED</option>
                    <option value="SHIPPED">SHIPPED</option>
                    <option value="PROCESSING">PROCESSING</option>
                    <option value="PENDING">PENDING</option>
                </select>
                
                <select
                    value={paymentStatusFilter}
                    onChange={(e) => setPaymentStatusFilter(e.target.value as typeof paymentStatusFilter)}
                    className="px-4 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg cursor-pointer"
                >
                    <option value="All Payment Statuses">All Payment Statuses</option>
                    <option value="PAID">PAID</option>
                    <option value="UNPAID">UNPAID</option>
                </select>

                <select
                    value={shippingStatusFilter}
                    onChange={(e) => setShippingStatusFilter(e.target.value as typeof shippingStatusFilter)}
                    className="px-4 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg cursor-pointer"
                >
                    <option value="All Shipping Statuses">All Shipping Statuses</option>
                    <option value="DELIVERED">DELIVERED</option>
                    <option value="IN TRANSIT">IN TRANSIT</option>
                    <option value="OUT FOR DELIVERY">OUT FOR DELIVERY</option>
                    <option value="READY FOR DISPATCH">READY FOR DISPATCH</option>
                </select>

                {/* Date Range Filter (Placeholder) */}
                 <select
                    className="px-4 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg cursor-pointer"
                >
                    <option value="All Dates">All Dates</option>
                    <option value="Today">Today</option>
                    <option value="Last 7 Days">Last 7 Days</option>
                </select>
            </div>
            
            {/* The Internal Verification Filter block is removed */}
            
        </div>


        {loading && <div className="text-white text-center p-8">Loading Orders...</div>}
        {error && <div className="p-4 rounded-lg bg-red-600 text-white text-center font-semibold">Error: {error}</div>}
        
        {/* Table View */}
        {!loading && !error && (
          <div className="overflow-x-auto bg-gray-800 rounded-lg shadow-xl border border-gray-700">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">ORDER ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">CUSTOMER NAME</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">ORDER DATE & TIME</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">TOTAL AMOUNT</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">ORDER STATUS</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">PAYMENT STATUS</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">SHIPPING STATUS</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">ACTIONS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {filteredOrders.map((order) => (
                  <tr 
                    key={order.id} 
                    className="hover:bg-gray-700 transition duration-150"
                  >
                    {/* ORDER ID */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-400">
                        #{order.id?.substring(0, 8) || 'N/A'}...
                    </td>
                    {/* CUSTOMER NAME */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white font-semibold">
                        {order.firstName || 'N/A'}
                    </td>
                    {/* ORDER DATE & TIME */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {formatDateTime(order.createdAt)}
                    </td>
                    {/* TOTAL PAYABLE */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-400">
                        ₹{order.payableAmount?.toFixed(2) || '0.00'}
                    </td>
                    {/* ORDER STATUS */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {getStatusBadge(order.orderStatus, getStatusColor(order.orderStatus))}
                    </td>
                    {/* PAYMENT STATUS */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {getStatusBadge(order.paymentStatus, getStatusColor(order.paymentStatus))}
                    </td>
                    {/* SHIPPING STATUS */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {getStatusBadge(order.shippingStatus, getStatusColor(order.shippingStatus))}
                    </td>
                    {/* ACTIONS */}
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-3">
                            {/* View Icon: Details Modal (Only button needed now) */}
                            <button 
                                onClick={() => handleViewDetails(order)}
                                className="text-blue-400 hover:text-blue-300 transition"
                                title="View All Details"
                            >
                                <FaEye className="w-5 h-5"/>
                            </button>
                        </div>
                    </td>
                  </tr>
                ))}
                {finalOrderCount === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-10 text-gray-400 text-xl">
                      No orders found matching the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Render Modal if an order is selected */}
      {selectedOrder && (
          <DetailsModal 
              order={selectedOrder} 
              onClose={() => setSelectedOrder(null)} 
          />
      )}
    </AdminLayout>
  );
};

export default SaleOrder;